import { flattenPositionConfig, MATCHPLAY_POSITION_SELECTOR_CONFIG } from "@Constants/game/position-config";
import PlayPositions from "@Enums/PlayPositions";
import StorageKeys from "@Enums/StorageKeys";

export const matchPlayPositions = Array.from(
  new Set(flattenPositionConfig(MATCHPLAY_POSITION_SELECTOR_CONFIG.positions).map(position => position.id))
);

export const matchPlayDefaults = {
  difficulty: {
    easy: "easy",
    medium: "medium",
    hard: "hard",
  },
  difficultyDefault: "medium" as const,
  speed: {
    slow: "slow",
    normal: "normal",
    fast: "fast",
  },
  speedDefault: "normal" as const,
  duration: {
    min: 1,
    default: 3,
    max: 5,
  },
};

export const matchPlaySettingValues = {
  difficulty: { easy: 0.25, medium: 0.5, hard: 0.75 },
  speed: { slow: 0.6, normal: 1, fast: 1.5 },
};

export const toMatchPlayPhysicsTime = (gameSpeed: number) => (2 - gameSpeed) * 10;

export const getMatchPlayDifficultyValue = (difficulty: MatchPlayDifficulty) => matchPlaySettingValues.difficulty[difficulty];
export const getMatchPlaySpeedValue = (speed: MatchPlaySpeed) => matchPlaySettingValues.speed[speed];
export const getMatchPlayDifficultyKey = (value: number): MatchPlayDifficulty =>
  (Object.entries(matchPlaySettingValues.difficulty).find(([, settingValue]) => settingValue === value)?.[0] as MatchPlayDifficulty) ??
  matchPlayDefaults.difficultyDefault;
export const getMatchPlaySpeedKey = (value: number): MatchPlaySpeed =>
  (Object.entries(matchPlaySettingValues.speed).find(([, settingValue]) => settingValue === value)?.[0] as MatchPlaySpeed) ?? matchPlayDefaults.speedDefault;

export type MatchPlayDifficulty = keyof typeof matchPlayDefaults.difficulty;
export type MatchPlaySpeed = keyof typeof matchPlayDefaults.speed;

export const loadMatchPlaySettings = () => {
  const defaults = {
    difficulty: matchPlayDefaults.difficultyDefault,
    gameSpeed: matchPlayDefaults.speedDefault,
    gameDuration: matchPlayDefaults.duration.default,
  };

  try {
    const stored = JSON.parse(window.localStorage.getItem(StorageKeys.MATCHPLAY_SETTINGS) || "null");
    return {
      difficulty: Object.keys(matchPlayDefaults.difficulty).includes(stored?.difficulty) ? stored.difficulty : defaults.difficulty,
      gameSpeed: Object.keys(matchPlayDefaults.speed).includes(stored?.gameSpeed) ? stored.gameSpeed : defaults.gameSpeed,
      gameDuration:
        Number.isInteger(stored?.gameDuration) && stored.gameDuration >= matchPlayDefaults.duration.min && stored.gameDuration <= matchPlayDefaults.duration.max
          ? stored.gameDuration
          : defaults.gameDuration,
    };
  } catch {
    return defaults;
  }
};

export const saveMatchPlaySettings = (settings: {
  difficulty: MatchPlayDifficulty;
  gameSpeed: MatchPlaySpeed;
  gameDuration: number;
}) => {
  window.localStorage.setItem(StorageKeys.MATCHPLAY_SETTINGS, JSON.stringify(settings));
};

export const getSpeedLabelKey = (num: number) => {
  if (num <= matchPlaySettingValues.speed.slow) return "match-play.settings.speed.slow";
  if (num <= matchPlaySettingValues.speed.normal) return "match-play.settings.speed.normal";
  return "match-play.settings.speed.fast";
};

export const getRandomMatchPlayPosition = (positions: PlayPositions[] = matchPlayPositions) =>
  positions[Math.floor(Math.random() * positions.length)]!;

export const getMatchPlayBackendPosition = (position: PlayPositions): PlayPositions => {
  switch (position) {
    case PlayPositions.LEFT_CENTER_BACK:
    case PlayPositions.RIGHT_CENTER_BACK:
      return PlayPositions.CENTER_BACK;
    case PlayPositions.LEFT_CENTER_MIDFIELDER:
    case PlayPositions.RIGHT_CENTER_MIDFIELDER:
      return PlayPositions.CENTER_MIDFIELDER;
    default:
      return position;
  }
};

export const getDifficultyLabelKey = (num: number) => {
  if (num <= matchPlaySettingValues.difficulty.easy) return "match-play.settings.difficulty.easy";
  if (num <= matchPlaySettingValues.difficulty.medium) return "match-play.settings.difficulty.medium";
  return "match-play.settings.difficulty.hard";
};
